/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FiPlus } from 'react-icons/fi'
import { TbPolygon } from 'react-icons/tb'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { Alert, Button, ConfirmModal, TableSkeleton, Toast } from '@/components/ui'
import { ListControls } from '@/components/ui/list-controls/ListControls'
import {
  LayoutSwitch,
  ListCounts,
  ListPageToolbar,
  type ListLayoutMode,
} from '@/components/ui/list-page/ListPageToolbar'
import { ListSearch } from '@/components/ui/list-page/ListSearch'
import { useApi, useToast } from '@/hooks'
import useMediaQuery from '@/hooks/useMediaQuery'
import { exportToExcel } from '@/utils'

import { deleteGeofence, writeFailure } from './api'
import { CompanyPicker } from './components/CompanyPicker'
import { GeofenceFilterDropdown } from './components/GeofenceFilterDropdown'
import { ZOrderPill } from './components/ZOrderPill'
import { activeFilterCount, applyFilters, EMPTY_FILTERS, readFilters, sortGeofences, writeFilters } from './filters'
import { GeofenceDetailSheet } from './GeofenceDetailSheet'
import { GeofenceList } from './GeofenceList'
import { GeofenceOverviewMap } from './GeofenceOverviewMap'
import {
  useGeofence,
  useGeofenceCategories,
  useGeofenceCompany,
  useGeofencesByBoundaries,
  useInvalidateGeofences,
  useManagedGeofences,
} from './hooks'
import {
  type GeofenceEditorNavigationState,
  type GeofenceFilters,
  type GeofenceMapViewport,
  type ManagedGeofence,
  NOT_CATEGORISED,
  type SortKey,
  type SortState,
} from './types'

const VISIBILITY_OPTIONS: { value: GeofenceFilters['visibility']; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'visible', label: 'Shown' },
  { value: 'hidden', label: 'Hidden' },
]

const LAYER_OPTIONS = [1, 2, 3, 4, 5].map((layer) => ({
  value: String(layer),
  label: `Layer ${layer}`,
  icon: <ZOrderPill zorder={layer} />,
}))

interface GeofencesViewProps {
  /** The open geofence, from the route (`/geofences/:geofenceId`). */
  openGeofenceId?: number
}

/**
 * The Geofences page: the company's geofences listed on the left and drawn on the map on the
 * right, with the open one's details beside the map. Filters and the company live in the query
 * string, so opening and closing a geofence keeps them.
 */
export const GeofencesView = ({ openGeofenceId }: GeofencesViewProps) => {
  const navigate = useNavigate()
  const { api } = useApi()
  const [searchParams, setSearchParams] = useSearchParams()
  const [companyId, setCompanyId] = useGeofenceCompany()
  const { toast, show, showToast, hideToast } = useToast()
  const invalidate = useInvalidateGeofences()
  const canEdit = useMediaQuery('(min-width: 768px)')

  const geofencesQuery = useManagedGeofences(companyId)
  const categoriesQuery = useGeofenceCategories(companyId)
  const geofences = useMemo(() => geofencesQuery.data ?? [], [geofencesQuery.data])
  const openGeofenceQuery = useGeofence(openGeofenceId)
  const categories = useMemo(
    () => new Map((categoriesQuery.data ?? []).map((category) => [category.id, category])),
    [categoriesQuery.data]
  )

  const filters = useMemo(() => readFilters(searchParams), [searchParams])
  const setFilters = useCallback(
    (next: GeofenceFilters) => setSearchParams((prev) => writeFilters(prev, next), { replace: true }),
    [setSearchParams]
  )
  const [sort, setSort] = useState<SortState>({ key: 'name', ascending: true })
  const [hoveredId, setHoveredId] = useState<number>()
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [layoutMode, setLayoutMode] = useState<ListLayoutMode>('map')
  const limitToMap = searchParams.get('limitToMap') === '1'
  const [mapViewport, setMapViewport] = useState<GeofenceMapViewport>()
  const boundaryQueryEnabled = limitToMap && layoutMode !== 'table'
  const boundariesQuery = useGeofencesByBoundaries(companyId, mapViewport, boundaryQueryEnabled)
  const toggleLimitToMap = useCallback(() => {
    if (!limitToMap) setMapViewport(undefined)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (limitToMap) next.delete('limitToMap')
        else next.set('limitToMap', '1')
        return next
      },
      { replace: true }
    )
  }, [limitToMap, setSearchParams])

  const previousCompanyId = useRef(companyId)
  useEffect(() => {
    const companyChanged = previousCompanyId.current !== undefined && previousCompanyId.current !== companyId
    previousCompanyId.current = companyId
    setMapViewport(undefined)
    if (companyChanged && openGeofenceId !== undefined) {
      navigate({ pathname: '/geofences', search: searchParams.toString() }, { replace: true })
    }
  }, [companyId, navigate, openGeofenceId, searchParams])

  // The editor comes back here after a save, naming what it saved.
  const location = useLocation()
  const savedGeofence = (location.state as { savedGeofence?: string } | null)?.savedGeofence
  useEffect(() => {
    if (!savedGeofence) return
    showToast({ header: 'Geofence saved', body: `“${savedGeofence}” was saved.`, position: 'end', success: true })
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null })
  }, [savedGeofence, showToast, navigate, location.pathname, location.search])

  const shown = useMemo(() => sortGeofences(applyFilters(geofences, filters), sort), [geofences, filters, sort])
  const listedOpenGeofence = geofences.find((geofence) => geofence.id === openGeofenceId)
  const openGeofence = useMemo<ManagedGeofence | undefined>(() => {
    const detail = openGeofenceQuery.data
    if (!detail) return listedOpenGeofence
    return {
      ...detail,
      coordinates: detail.points.map((point) => [point.latitude, point.longitude] as [number, number]),
    }
  }, [listedOpenGeofence, openGeofenceQuery.data])
  const boundaryIds = useMemo(() => new Set(boundariesQuery.data?.ids ?? []), [boundariesQuery.data?.ids])
  const displayedGeofences = useMemo(() => {
    if (!boundaryQueryEnabled || boundariesQuery.data === undefined) return shown
    return shown.filter((geofence) => boundaryIds.has(geofence.id))
  }, [boundariesQuery.data, boundaryIds, boundaryQueryEnabled, shown])
  const boundaryGeofences = useMemo(
    () => new Map((boundariesQuery.data?.geofences ?? []).map((geofence) => [geofence.id, geofence])),
    [boundariesQuery.data?.geofences]
  )
  const mapGeofences = useMemo(() => {
    if (!boundaryQueryEnabled || boundariesQuery.data === undefined) return openGeofence ? [openGeofence] : []

    // Keep every polygon in the current region. Add the selected detail geometry without moving
    // the camera, so selecting a row does not undo the LocationMap-style viewport behavior.
    const polygons = displayedGeofences
      .map((geofence) => boundaryGeofences.get(geofence.id))
      .filter((geofence): geofence is ManagedGeofence => !!geofence && geofence.coordinates.length >= 3)
    if (openGeofence && openGeofence.coordinates.length >= 3) {
      const polygonsById = new Map(polygons.map((geofence) => [geofence.id, geofence]))
      polygonsById.set(openGeofence.id, openGeofence)
      return [...polygonsById.values()]
    }
    return polygons
  }, [boundariesQuery.data, boundaryGeofences, boundaryQueryEnabled, displayedGeofences, openGeofence])
  const openPath = useCallback(
    (path: string, state?: GeofenceEditorNavigationState) =>
      navigate({ pathname: path, search: searchParams.toString() }, { state }),
    [navigate, searchParams]
  )

  const handleSort = (key: SortKey) =>
    setSort((prev) => ({ key, ascending: prev.key === key ? !prev.ascending : true }))
  const categoryOptions = useMemo(
    () => [
      { value: NOT_CATEGORISED, label: 'Not categorised' },
      ...(categoriesQuery.data ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    ],
    [categoriesQuery.data]
  )

  const handleDelete = async () => {
    if (!openGeofence) return
    setDeleting(true)
    try {
      await deleteGeofence(api, openGeofence.id, openGeofence.version)
      await invalidate()
      setConfirmDelete(false)
      openPath('/geofences')
      showToast({
        header: 'Geofence deleted',
        body: `“${openGeofence.name}” was deleted.`,
        position: 'end',
        success: true,
      })
    } catch (error) {
      const failure = writeFailure(error)
      setConfirmDelete(false)
      showToast(
        failure.kind === 'unavailable'
          ? {
              header: 'Not available yet',
              body: 'Deleting geofences isn’t available in Insights yet. Use Refresh for now.',
              position: 'end',
              info: true,
            }
          : failure.kind === 'conflict'
            ? {
                header: 'Geofence changed',
                body: 'Someone changed this geofence since it was loaded. Check it and try again.',
                position: 'end',
                warning: true,
              }
            : { header: 'Geofence not deleted', body: failure.message, position: 'end', warning: true }
      )
      if (failure.kind === 'conflict') void invalidate()
    } finally {
      setDeleting(false)
    }
  }

  const downloadDisabled = !companyId || (boundaryQueryEnabled && (!mapViewport || boundariesQuery.isFetching))

  const handleDownload = () => {
    exportToExcel(
      { name: 'Geofence', zorder: 'Z-order', visible: 'Visible' },
      displayedGeofences.map((geofence) => ({
        name: geofence.name,
        zorder: geofence.zorder,
        visible: geofence.visible ? 'Shown' : 'Hidden',
      })),
      'Cooltrax_Geofences'
    )
  }

  const isLoading = !!companyId && (geofencesQuery.isLoading || categoriesQuery.isLoading)
  const filtered = activeFilterCount(filters) > 0

  const body = !companyId ? (
    <EmptyState title="Choose a company" text="Pick a company above to see its geofences." />
  ) : isLoading ? (
    <div className="p-2">
      <TableSkeleton columns={3} rows={12} rowHeight={44} />
    </div>
  ) : geofencesQuery.isError ? (
    <div className="p-4">
      <Alert
        type="error"
        message="Couldn’t load geofences"
        description={geofencesQuery.error instanceof Error ? geofencesQuery.error.message : 'Please try again.'}
        alertActions={
          <Button variation="outline" size="sm" handleClick={() => void geofencesQuery.refetch()}>
            Try again
          </Button>
        }
      />
    </div>
  ) : geofences.length === 0 ? (
    <EmptyState
      title="No geofences yet"
      text="Draw the first one for this company."
      action={
        canEdit && (
          <Button variation="primary" size="sm" icon={<FiPlus />} handleClick={() => openPath('/geofences/new')}>
            New geofence
          </Button>
        )
      }
    />
  ) : displayedGeofences.length === 0 ? (
    <EmptyState
      title="No geofences match"
      text="Try another search or clear the filters."
      action={
        <Button variation="primary" size="sm" handleClick={() => setFilters(EMPTY_FILTERS)}>
          Clear filters
        </Button>
      }
    />
  ) : (
    <GeofenceList
      geofences={displayedGeofences}
      sort={sort}
      onSort={handleSort}
      selectedId={openGeofenceId}
      onSelect={(id) => openPath(`/geofences/${id}`)}
      onHover={setHoveredId}
    />
  )

  const mapView = (
    <GeofenceOverviewMap
      companyId={companyId}
      geofences={mapGeofences}
      selectedId={openGeofenceId}
      hoveredId={hoveredId}
      onSelect={(id) => openPath('/geofences/' + id)}
      limitToMap={boundaryQueryEnabled}
      onViewportChange={setMapViewport}
    />
  )

  const detailContent = openGeofence ? (
    <GeofenceDetailSheet
      geofence={openGeofence}
      categories={categories}
      canEdit={canEdit && !!openGeofenceQuery.data}
      onEdit={() => openPath('/geofences/' + openGeofence.id + '/edit', { geofence: openGeofenceQuery.data })}
      onDelete={() => setConfirmDelete(true)}
    />
  ) : null

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ListPageToolbar
        title="Geofences"
        sheetIsOpen={false}
        savedViews={null}
        search={
          <ListSearch
            id="search-geofences"
            placeholder="Search geofences..."
            value={filters.search}
            onChange={(search) => setFilters({ ...filters, search })}
          />
        }
        counts={
          <ListCounts
            count={
              displayedGeofences.length.toLocaleString() + ' of ' + geofences.length.toLocaleString() + ' geofences'
            }
          />
        }
        scope={<CompanyPicker companyId={companyId} onChange={setCompanyId} />}
        listControls={
          <ListControls
            limitToMap={
              layoutMode !== 'table'
                ? {
                    isOn: limitToMap,
                    onToggle: toggleLimitToMap,
                  }
                : undefined
            }
            onDownload={handleDownload}
            disabled={downloadDisabled}
          />
        }
        layoutSwitch={<LayoutSwitch mode={layoutMode} onChange={(next) => setLayoutMode(next)} />}
        filters={
          <>
            <GeofenceFilterDropdown
              label="Category"
              options={categoryOptions}
              selected={filters.categories}
              onChange={(categories) => setFilters({ ...filters, categories })}
              minWidth="min-w-[160px]"
            />
            <GeofenceFilterDropdown
              label="Z-order"
              options={LAYER_OPTIONS.map((option) => ({ value: String(option.value), label: option.label }))}
              selected={filters.layers.map(String)}
              onChange={(layers) => setFilters({ ...filters, layers: layers.map(Number).sort() })}
            />
            <GeofenceFilterDropdown
              label="Visibility"
              options={VISIBILITY_OPTIONS}
              selected={[filters.visibility]}
              onChange={(next) =>
                setFilters({
                  ...filters,
                  visibility: (next[0] as GeofenceFilters['visibility'] | undefined) ?? 'all',
                })
              }
              multiple={false}
              minWidth="min-w-[145px]"
            />
          </>
        }
        actions={
          <>
            {filtered && (
              <Button variation="ghost" size="sm" handleClick={() => setFilters(EMPTY_FILTERS)}>
                Clear filters
              </Button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-primary shrink-0"
              disabled={!companyId || !canEdit}
              onClick={() => openPath('/geofences/new')}
              data-testid="new-geofence"
            >
              <FiPlus className="h-4 w-4" />
              New Geofence
            </button>
          </>
        }
      />

      <div className="relative mt-3 flex min-h-0 flex-1 overflow-hidden rounded-box border border-base-300 bg-base-100">
        {layoutMode === 'table' ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">{body}</div>
        ) : layoutMode === 'split' ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="flex min-h-0 min-w-0 flex-1">
              <div className="relative min-h-0 min-w-0 flex-1">{mapView}</div>
              {detailContent && (
                <div className="relative min-h-0 w-1/4 min-w-0 shrink-0 border-l border-base-300">{detailContent}</div>
              )}
            </div>
            <div className="h-1.5 shrink-0 bg-base-300" />
            <div className="min-h-0 min-w-0 flex-1">{body}</div>
          </div>
        ) : (
          <div className="flex min-h-0 min-w-0 flex-1">
            <div className="flex w-[350px] min-w-0 shrink-0 flex-col border-r border-base-300">{body}</div>
            <div className="relative hidden min-w-0 flex-1 md:block">{mapView}</div>
            {detailContent && (
              <div className="absolute inset-0 z-40 md:static md:flex md:w-96 md:shrink-0">{detailContent}</div>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        show={confirmDelete}
        title="Delete geofence"
        variation="danger"
        confirmLabel="Delete"
        isLoading={deleting}
        body={
          <p>
            Delete <strong>{openGeofence?.name}</strong>? Alerts and reports that use this geofence will stop matching
            it.
          </p>
        }
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
      <Toast {...toast} show={show} onClose={hideToast} />
    </div>
  )
}

const EmptyState = ({ title, text, action }: { title: string; text: string; action?: React.ReactNode }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
    <TbPolygon className="text-4xl text-base-content/30" />
    <p className="text-lg font-medium">{title}</p>
    <p className="max-w-sm text-sm text-base-content/60">{text}</p>
    {action}
  </div>
)
